import React, { Suspense, useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTutorial } from '../context/TutorialContext';
import StoryTimeWizard from '../components/StoryTimeWizard';
import AudioPlayer from '../components/AudioPlayer';
import AudioItem from '../components/AudioItem';
import AppDock from '../components/AppDock';
import HabitHero from '../components/HabitHero';
import StoryTimeHero from '../components/StoryTimeHero';
import CreditDisplay from '../components/CreditDisplay';
import wasabiService from '../services/WasabiService';
import CreditService from '../services/CreditService';
import { READING_LISTS, MIN_PLAYLIST_ROW_BOOKS, listMatchesTitle, listKeyRank } from '../utils/readingLists';
import { getApiUrl } from '../services/apiConfig';
import lazyWithReload from '../utils/lazyWithReload';
import { useOffline } from '../offline/OfflineContext';
import InstallBanner from '../offline/InstallBanner';
import ByopdfLoader from '../components/ByopdfLoader';
import styles from './Dashboard.module.css';

import {
  RiHome4Line,
  RiShoppingBag3Line,
  RiQuestionLine,
  RiBookOpenLine,
  RiUser3Line,
  RiLogoutBoxRLine,
  RiSettings4Line,
  RiBarChart2Line,
  RiMedalLine,
  RiAddLine,
  RiMoonLine,
  RiSunLine,
  RiArrowLeftSLine,
  RiArrowUpDownLine,
  RiArrowRightSLine,
  RiSearchLine,
  RiCloseLine,
  RiSparkling2Line
} from 'react-icons/ri';

// Split out of the main bundle: the uploaders carry PDF.js and jszip, the
// tour carries react-joyride. The uploader code is prefetched on idle so the
// first "New audiobook" tap does not wait on the network.
// The prefetch is a plain import: a failed background fetch must not reload
// the page. Only the real open goes through the reload-once safeguard.
const loadContentUploader = () => import('../components/ContentUploader');
const loadPremiumContentUploader = () => import('../components/PremiumContentUploader');
const ContentUploader = lazyWithReload(loadContentUploader);
const PremiumContentUploader = lazyWithReload(loadPremiumContentUploader);
const InteractiveTour = lazyWithReload(() => import('../components/InteractiveTour'));

const TERMINAL_JOB_STATUSES = new Set(['ready', 'completed', 'failed']);
const ACTIVE_POLL_INTERVAL_MS = 8000;
const IDLE_POLL_INTERVAL_MS = 30000;
const CURATED_SHELF_KEYS = {
  GENERAL: 'general',
  CLASSICS: 'classics',
  SCIENCE: 'science',
  GRAPHIC: 'graphic',
  SHORT_STORIES: 'short_stories',
  EXPLAINERS: 'explainers',
  STORY_TIME: 'story_time',
  // Gated series: episode 1 plays for everyone, the rest need a subscription.
  STORY_TIME_PREMIUM: 'story_time_premium'
};

// Shelves for the "by type" library view. Order = display order; uploads
// without a documentType (older books) land on the Books shelf.
const DOC_TYPE_SHELVES = [
  { type: 'story_time', title: 'Story Time', hint: 'Series we wrote and performed from your ideas' },
  { type: 'book', title: 'Your Books', hint: 'Books you uploaded and generated' },
  { type: 'article', title: 'Articles & Essays', hint: 'Shorter reads you converted' },
  { type: 'report', title: 'Reports', hint: 'Work documents and reports' },
  { type: 'research_paper', title: 'Research Papers', hint: 'Academic papers and studies' },
  { type: 'notes', title: 'Notes & Other', hint: 'Everything else you converted' }
];

const LIBRARY_SORT_OPTIONS = [
  { value: 'recent', label: 'Recent' },
  { value: 'listened', label: 'Last listened' },
  { value: 'type', label: 'By type' }
];
const CURATED_SHELF_ALIASES = {
  general: CURATED_SHELF_KEYS.GENERAL,
  curated: CURATED_SHELF_KEYS.GENERAL,
  featured: CURATED_SHELF_KEYS.GENERAL,
  picks: CURATED_SHELF_KEYS.GENERAL,
  classics: CURATED_SHELF_KEYS.CLASSICS,
  classic: CURATED_SHELF_KEYS.CLASSICS,
  literature: CURATED_SHELF_KEYS.CLASSICS,
  science: CURATED_SHELF_KEYS.SCIENCE,
  scientific: CURATED_SHELF_KEYS.SCIENCE,
  research: CURATED_SHELF_KEYS.SCIENCE,
  paper: CURATED_SHELF_KEYS.SCIENCE,
  papers: CURATED_SHELF_KEYS.SCIENCE,
  graphic: CURATED_SHELF_KEYS.GRAPHIC,
  graphics: CURATED_SHELF_KEYS.GRAPHIC,
  comic: CURATED_SHELF_KEYS.GRAPHIC,
  comics: CURATED_SHELF_KEYS.GRAPHIC,
  manga: CURATED_SHELF_KEYS.GRAPHIC,
  excerpt: CURATED_SHELF_KEYS.GRAPHIC,
  excerpts: CURATED_SHELF_KEYS.GRAPHIC,
  short_stories: CURATED_SHELF_KEYS.SHORT_STORIES,
  short_story: CURATED_SHELF_KEYS.SHORT_STORIES,
  shortstories: CURATED_SHELF_KEYS.SHORT_STORIES,
  shortstory: CURATED_SHELF_KEYS.SHORT_STORIES,
  story: CURATED_SHELF_KEYS.SHORT_STORIES,
  stories: CURATED_SHELF_KEYS.SHORT_STORIES,
  explainers: CURATED_SHELF_KEYS.EXPLAINERS,
  explainer: CURATED_SHELF_KEYS.EXPLAINERS,
  guide: CURATED_SHELF_KEYS.EXPLAINERS,
  guides: CURATED_SHELF_KEYS.EXPLAINERS,
  tour: CURATED_SHELF_KEYS.EXPLAINERS,
  tours: CURATED_SHELF_KEYS.EXPLAINERS,
  story_time: CURATED_SHELF_KEYS.STORY_TIME,
  storytime: CURATED_SHELF_KEYS.STORY_TIME,
  story_time_premium: CURATED_SHELF_KEYS.STORY_TIME_PREMIUM,
  storytimepremium: CURATED_SHELF_KEYS.STORY_TIME_PREMIUM
};

const normalizeStatus = (status) => String(status || '').toLowerCase();

const isInProgressStatus = (status) => {
  const normalized = normalizeStatus(status);
  return normalized ? !TERMINAL_JOB_STATUSES.has(normalized) : false;
};

const isChapterLikelyPlayable = (chapter, completedChapters = 0) => {
  if (!chapter || typeof chapter !== 'object') return false;
  const chapterStatus = normalizeStatus(chapter.status);
  if (['ready', 'completed', 'tts_complete', 'uploading'].includes(chapterStatus)) {
    return true;
  }

  if (chapter.audioUrl || chapter.audio_url || chapter.audioKey || chapter.audio_key) {
    return true;
  }

  const chapterOrder = Number(chapter.order || chapter.chapter_order || 0);
  const chapterIdNumber = Number(chapter.id);
  const completed = Number(completedChapters || 0);
  if (completed > 0 && (
    (Number.isFinite(chapterOrder) && chapterOrder > 0 && chapterOrder <= completed) ||
    (Number.isFinite(chapterIdNumber) && chapterIdNumber > 0 && chapterIdNumber <= completed)
  )) {
    return true;
  }

  return false;
};

const canOpenInProgressBook = (book) => {
  if (!book) return false;
  if (!isInProgressStatus(book.status)) return true;

  const completedChapters = Number(book.completedChapters || 0);
  if (completedChapters > 0) return true;

  const chapters = Array.isArray(book.chapters) ? book.chapters : [];
  return chapters.some((chapter) => isChapterLikelyPlayable(chapter, completedChapters));
};

const toPositiveNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const sumChapterDurations = (chapters) => {
  if (!Array.isArray(chapters) || chapters.length === 0) return 0;
  return chapters.reduce((total, chapter) => {
    if (!chapter || typeof chapter !== 'object') return total;
    return total + toPositiveNumber(chapter.duration);
  }, 0);
};

const resolveTotalDuration = (totalDurationValue, chapters) => {
  const directDuration = toPositiveNumber(totalDurationValue);
  if (directDuration > 0) {
    return directDuration;
  }
  return sumChapterDurations(chapters);
};

const transformLibraryItemsToBooks = (items) => {
  return (items || [])
    .map(item => {
      const normalizedChapters = Array.isArray(item.chapters) && item.chapters.length > 0
        ? item.chapters.map((chapter, idx) => ({
          ...chapter,
          id: String(chapter?.id ?? chapter?.chapterId ?? idx + 1),
          title: chapter?.title || `Chapter ${idx + 1}`,
          order: Number(chapter?.order ?? chapter?.chapter_order ?? idx + 1),
          duration: Number(chapter?.duration || 0),
          status: chapter?.status || '',
          audioUrl: chapter?.audioUrl || chapter?.audio_url || chapter?.audioKey || chapter?.audio_key || '',
          audio_url: chapter?.audio_url || chapter?.audioUrl || chapter?.audioKey || chapter?.audio_key || '',
          transcriptUrl: chapter?.transcriptUrl || chapter?.transcript_url || chapter?.transcriptKey || chapter?.transcript_key || '',
          transcript_url: chapter?.transcript_url || chapter?.transcriptUrl || chapter?.transcriptKey || chapter?.transcript_key || '',
        }))
        : Array.from({ length: Number(item.totalChapters || 0) }, (_, idx) => ({
          id: String(idx + 1),
          title: `Chapter ${idx + 1}`
        }));
      return {
        id: item.id,
        name: item.title || 'Untitled',
        chapters: normalizedChapters,
        createdAt: item.createdAt,
        status: item.status || 'processing',
        statusRaw: item.statusRaw || item.status || 'processing',
        progress: item.progress || 0,
        completedChapters: Number(item.completedChapters || 0),
        totalChapters: Number(item.totalChapters || item.chapters?.length || 0),
        jobId: item.id,
        totalDuration: resolveTotalDuration(item.totalDuration, normalizedChapters),
        documentType: item.documentType || item.document_type || '',
      seriesRoot: item.seriesRoot || null,
      season: item.season || null,
        seriesRoot: item.seriesRoot || null,
        season: item.season || null,
        coverUrl: item.coverUrl,
      coverThumbUrl: item.coverThumbUrl || '',
        coverThumbUrl: item.coverThumbUrl || '',
        fallbackQuote: item.fallbackQuote,
        queuePosition: item.queuePosition,
        queueAhead: item.queueAhead,
        queueEtaMinutes: item.queueEtaMinutes,
        queueEtaMinMinutes: item.queueEtaMinMinutes,
        queueEtaMaxMinutes: item.queueEtaMaxMinutes,
        playbackState: item.playbackState || 'new',
        playbackPercent: Number(item.playbackPercent || 0),
        lastPlayedAt: item.lastPlayedAt || null,
        isCurated: false
      };
    })
    .sort((a, b) => {
      const aTime = new Date(a.createdAt || 0).getTime();
      const bTime = new Date(b.createdAt || 0).getTime();
      return bTime - aTime;
    });
};

const mergeMetadataIntoBooks = (books, metadataItems) => {
  const metadataMap = {};
  metadataItems.forEach(item => {
    if (item?.id) {
      metadataMap[item.id] = item;
    }
  });

  const mergedBooks = books.map(book => {
    const metadata = metadataMap[book.id];
    if (!metadata) {
      return book;
    }

    return {
      ...book,
      name: metadata.title || book.name,
      createdAt: metadata.createdAt || book.createdAt,
      status: metadata.status || book.status,
      statusRaw: metadata.statusRaw || book.statusRaw || metadata.status || book.status,
      progress: metadata.progress ?? book.progress,
      completedChapters: Number(metadata.completedChapters ?? book.completedChapters ?? 0),
      totalChapters: Number(metadata.totalChapters ?? book.totalChapters ?? book.chapters?.length ?? 0),
      jobId: metadata.id || book.jobId || book.id,
      totalDuration: resolveTotalDuration(metadata.totalDuration ?? book.totalDuration, book.chapters),
      documentType: metadata.documentType || metadata.document_type || book.documentType || '',
      coverUrl: metadata.coverUrl || book.coverUrl,
      fallbackQuote: metadata.fallbackQuote || book.fallbackQuote,
      queuePosition: metadata.queuePosition ?? book.queuePosition,
      queueAhead: metadata.queueAhead ?? book.queueAhead,
      queueEtaMinutes: metadata.queueEtaMinutes ?? book.queueEtaMinutes,
      queueEtaMinMinutes: metadata.queueEtaMinMinutes ?? book.queueEtaMinMinutes,
      queueEtaMaxMinutes: metadata.queueEtaMaxMinutes ?? book.queueEtaMaxMinutes,
      playbackState: metadata.playbackState || book.playbackState || 'new',
      playbackPercent: Number(metadata.playbackPercent ?? book.playbackPercent ?? 0)
    };
  });

  const mergedIds = new Set(mergedBooks.map(book => book.id));
  const processingOnlyBooks = metadataItems
    .filter(item => item?.id && !mergedIds.has(item.id) && isInProgressStatus(item.status))
    .map(item => ({
      id: item.id,
      name: item.title || 'Untitled',
      chapters: Array.from({ length: item.totalChapters || 0 }, (_, idx) => ({
        id: String(idx + 1),
        title: `Chapter ${idx + 1}`
      })),
      createdAt: item.createdAt,
      status: item.status || 'processing',
      statusRaw: item.statusRaw || item.status || 'processing',
      progress: item.progress || 0,
      completedChapters: Number(item.completedChapters || 0),
      totalChapters: Number(item.totalChapters || 0),
      jobId: item.id,
      totalDuration: resolveTotalDuration(item.totalDuration, item.chapters),
      documentType: item.documentType || item.document_type || '',
      seriesRoot: item.seriesRoot || null,
      season: item.season || null,
      coverUrl: item.coverUrl,
      coverThumbUrl: item.coverThumbUrl || '',
      fallbackQuote: item.fallbackQuote,
      queuePosition: item.queuePosition,
      queueAhead: item.queueAhead,
      queueEtaMinutes: item.queueEtaMinutes,
      queueEtaMinMinutes: item.queueEtaMinMinutes,
      queueEtaMaxMinutes: item.queueEtaMaxMinutes,
      playbackState: item.playbackState || 'new',
      playbackPercent: Number(item.playbackPercent || 0),
      isCurated: false
    }));

  return [...processingOnlyBooks, ...mergedBooks].sort((a, b) => {
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    return bTime - aTime;
  });
};

const normalizeSearch = (value) => String(value || '').trim().toLowerCase();

const normalizeCuratedShelf = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return CURATED_SHELF_ALIASES[normalized] || '';
};

const getCandidateShelfValues = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  const raw = String(value || '').trim();
  if (!raw) {
    return [];
  }
  return raw.split(',').map(part => part.trim()).filter(Boolean);
};

const resolveCuratedShelf = (book) => {
  const directFields = [book?.curatedShelf, book?.curatedCategory, book?.shelf, book?.category];
  for (const field of directFields) {
    for (const candidate of getCandidateShelfValues(field)) {
      const normalized = normalizeCuratedShelf(candidate);
      if (normalized) {
        return normalized;
      }
    }
  }

  const tags = getCandidateShelfValues(book?.tags);
  for (const tag of tags) {
    const normalized = normalizeCuratedShelf(tag);
    if (normalized) {
      return normalized;
    }
  }

  return CURATED_SHELF_KEYS.GENERAL;
};

const toNonNegativeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const sortedChaptersForPlayback = (chapters) => {
  if (!Array.isArray(chapters)) {
    return [];
  }
  return chapters
    .map((chapter, index) => ({
      ...chapter,
      id: String(chapter?.id ?? chapter?.chapterId ?? index + 1),
      order: Number(chapter?.order ?? chapter?.chapter_order ?? index + 1) || (index + 1),
      duration: toNonNegativeNumber(chapter?.duration)
    }))
    .sort((a, b) => a.order - b.order);
};

const computeCuratedPlaybackMetrics = (book, playbackState) => {
  const chapters = sortedChaptersForPlayback(book?.chapters);
  const totalDurationSeconds = resolveTotalDuration(book?.totalDuration, chapters);
  const lastPlayedPosition = Math.max(0, Number(playbackState?.lastPlayedPosition || 0));
  const lastChapterId = playbackState?.lastChapterId != null ? String(playbackState.lastChapterId) : '';
  const lastChapterIndex = lastChapterId
    ? chapters.findIndex((chapter) => String(chapter?.id) === lastChapterId)
    : -1;

  let listenedSeconds = 0;
  if (lastChapterIndex >= 0) {
    listenedSeconds = chapters
      .slice(0, lastChapterIndex)
      .reduce((total, chapter) => total + toNonNegativeNumber(chapter?.duration), 0);

    const activeChapterDuration = toNonNegativeNumber(chapters[lastChapterIndex]?.duration);
    listenedSeconds += activeChapterDuration > 0
      ? Math.min(lastPlayedPosition, activeChapterDuration)
      : lastPlayedPosition;
  }

  let playbackPercent = 0;
  if (playbackState?.isComplete) {
    playbackPercent = 100;
  } else if (totalDurationSeconds > 0) {
    playbackPercent = Math.max(0, Math.min(100, (listenedSeconds / totalDurationSeconds) * 100));
  } else if (chapters.length > 0 && lastChapterIndex >= 0) {
    const chapterProgress = lastChapterIndex + (lastPlayedPosition > 0 ? 0.5 : 0);
    playbackPercent = Math.max(0, Math.min(99, (chapterProgress / chapters.length) * 100));
  }

  const normalizedPercent = Number(playbackPercent.toFixed(2));
  const derivedState = playbackState?.isComplete || normalizedPercent >= 99.5
    ? 'read'
    : (normalizedPercent >= 0.5 || lastChapterIndex >= 0 || lastPlayedPosition > 0
      ? 'in_progress'
      : 'new');

  return {
    playbackState: derivedState,
    playbackPercent: normalizedPercent
  };
};

const transformCuratedBooks = (curatedBooksData) => {
  return (curatedBooksData || []).map(book => {
    let chapters = book.chapters;
    if (!Array.isArray(chapters)) {
      chapters = chapters ? [chapters] : [];
    }
    if (chapters.length === 0) {
      chapters = [{ id: 'default_chapter_1', title: 'Chapter 1' }];
    }

    const totalDuration = resolveTotalDuration(book.totalDuration, chapters);
    const base = {
      ...book,
      id: book.id || `curated_${Date.now()}_${Math.random()}`,
      name: book.title || 'Untitled Curated Book',
      chapters,
      createdAt: book.createdAt || new Date().toISOString(),
      status: 'completed',
      totalDuration,
      // Prefer the presigned URL from the aggregate endpoint (loads straight
      // from storage, in parallel); fall back to the public cover proxy.
      // A CDN or presigned URL loads as-is. The cover proxy is only for
      // references the browser cannot load itself: it costs a backend
      // request per cover (catalog lookup + storage check), which is what
      // made the shelf crawl when every catalog cover went through it.
      coverUrl: wasabiService.isDirectImageUrl(book.coverUrl)
        ? book.coverUrl
        : (book.id
          ? `${process.env.REACT_APP_API_URL}/api/curated-cover/${encodeURIComponent(book.id)}`
          : book.coverUrl),
      coverThumbUrl: wasabiService.isDirectImageUrl(book.coverThumbUrl) ? book.coverThumbUrl : '',
      fallbackQuote: book.fallbackQuote,
      curatedShelf: resolveCuratedShelf(book),
      isCurated: true,
      // Premium series live under their own catalog root (never on the CDN);
      // the manifest flag is the belt to that brace.
      isPremium: Boolean(book.premium) || String(book.catalogRoot || '') === 'story_time_premium'
    };
    const metrics = computeCuratedPlaybackMetrics(base, book.playback || null);
    return { ...base, ...metrics };
  }).filter(book => book.id);
};

// The backend re-signs cover URLs on every request, so the URL string changes
// (new X-Amz-Date/signature) even though the image doesn't. If we pass that
// straight into state, every poll cycle swaps every <img src>, forcing a
// refetch + repaint of all covers — the dashboard visibly flickers every few
// seconds. Presigned links live 12h, so within a session it's always safe to
// keep the URL we already rendered when it points at the same object.
const coverObjectIdentity = (url) => {
  const raw = String(url || '');
  if (!raw) return '';
  try {
    const parsed = new URL(raw, window.location.origin);
    return `${parsed.origin}${parsed.pathname}`; // signature lives in the query
  } catch (error) {
    return raw;
  }
};

// A presigned link carries its own expiry. The saved dashboard copy can be
// days old, so a link from it may be dead: never prefer a dead link over the
// fresh one the server just sent.
const signedUrlUsable = (url) => {
  const raw = String(url || '');
  if (!raw.includes('X-Amz-')) return true;
  try {
    const params = new URL(raw).searchParams;
    const stamp = String(params.get('X-Amz-Date') || '').match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
    const lifetime = Number(params.get('X-Amz-Expires'));
    if (!stamp || !Number.isFinite(lifetime)) return false;
    const signedAt = Date.UTC(+stamp[1], +stamp[2] - 1, +stamp[3], +stamp[4], +stamp[5], +stamp[6]);
    return Date.now() < signedAt + (lifetime * 1000) - (30 * 60 * 1000);
  } catch (error) {
    return false;
  }
};

const stabilizeCoverUrls = (prevBooks, nextBooks) => {
  if (!Array.isArray(prevBooks) || prevBooks.length === 0) return nextBooks;
  const prevById = new Map(prevBooks.map((book) => [book.id, book]));
  return nextBooks.map((book) => {
    const prev = prevById.get(book.id);
    if (!prev?.coverUrl || !book.coverUrl || prev.coverUrl === book.coverUrl) return book;
    if (coverObjectIdentity(prev.coverUrl) !== coverObjectIdentity(book.coverUrl)) return book;
    if (!signedUrlUsable(prev.coverUrl)) return book;
    return {
      ...book,
      coverUrl: prev.coverUrl,
      coverThumbUrl: (prev.coverThumbUrl && signedUrlUsable(prev.coverThumbUrl)) ? prev.coverThumbUrl : book.coverThumbUrl
    };
  });
};

// Last-known dashboard payload, per user, for instant first paint.
// v2: adds habit insights + per-book lastPlayedAt (v1 entries lack both).
const DASHBOARD_CACHE_PREFIX = 'byopdf:dashboard:v2:';
const LEGACY_DASHBOARD_CACHE_PREFIX = 'byopdf:dashboard:v1:';

const readDashboardCache = (uid) => {
  try {
    const raw = window.localStorage.getItem(`${DASHBOARD_CACHE_PREFIX}${uid}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.audiobooks) || !Array.isArray(parsed.curatedBooks)) {
      return null;
    }
    return { ...parsed, insights: parsed.insights || null, subscriptionActive: Boolean(parsed.subscriptionActive) };
  } catch (error) {
    return null;
  }
};

const writeDashboardCache = (uid, audiobooks, curatedBooks, insights, subscriptionActive = false) => {
  try {
    window.localStorage.setItem(
      `${DASHBOARD_CACHE_PREFIX}${uid}`,
      JSON.stringify({
        audiobooks,
        curatedBooks,
        insights: insights || null,
        subscriptionActive: Boolean(subscriptionActive),
        savedAt: Date.now()
      })
    );
    window.localStorage.removeItem(`${LEGACY_DASHBOARD_CACHE_PREFIX}${uid}`);
  } catch (error) {
    // Quota/private mode failures are fine — the cache is best effort.
  }
};

function Dashboard() {
  const authContext = useAuth();
  const { currentUser, logout } = authContext;
  const offline = useOffline();

  // Launched with no network inside the installed app: go straight to the
  // downloaded books rather than a library that cannot load.
  useEffect(() => {
    if (offline.standalone && !offline.online) navigate('/downloads', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { toggleTheme, isDark } = useTheme();
  const { hasCompletedTutorial, startTour, resetTutorial, startPlayerTour, resetPlayerTutorial } = useTutorial();
  const navigate = useNavigate();
  const [audiobooks, setAudiobooks] = useState([]);
  const [curatedBooks, setCuratedBooks] = useState([]);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [selectedAudioData, setSelectedAudioData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [showStoryTime, setShowStoryTime] = useState(false);
  const [uploaderMode, setUploaderMode] = useState('standard');
  const premiumUploaderEnabled = process.env.REACT_APP_ENABLE_PREMIUM_UPLOADER === 'true';
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingCurated, setLoadingCurated] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [removingIds, setRemovingIds] = useState(() => new Set());
  const [hoveredItem, setHoveredItem] = useState(null);
  const [openingAudioKey, setOpeningAudioKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Library ordering: 'type' (one shelf per document type, the default so the
  // library stays organized), 'recent' (created), or 'listened' (last played).
  // Persisted so an explicit choice sticks.
  const [librarySort, setLibrarySort] = useState(() => {
    try {
      const stored = localStorage.getItem('byopdf:librarySort');
      return ['recent', 'listened', 'type'].includes(stored) ? stored : 'type';
    } catch {
      return 'type';
    }
  });

  const handleLibrarySortChange = (value) => {
    setLibrarySort(value);
    try {
      localStorage.setItem('byopdf:librarySort', value);
    } catch {
      // Private browsing: the choice just won't persist.
    }
  };
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [insights, setInsights] = useState(null);
  // Live subscription, from /api/dashboard. Only the Story Time Premium locks
  // read it; nothing else on the page changes with it.
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const audiobooksRef = useRef([]);
  const curatedBooksRef = useRef([]);
  const insightsRef = useRef(null);
  const shelfRefs = useRef({});
  const searchInputRef = useRef(null);

  useEffect(() => {
    console.log('Dashboard mounted, currentUser:', currentUser?.uid);
    wasabiService.setAuthContext(authContext);
    CreditService.setAuthContext(authContext);
  }, [authContext]);

  // Warm the uploader chunk once the shelf has painted.
  useEffect(() => {
    if (loading) return undefined;
    const prefetch = () => {
      loadContentUploader().catch(() => {});
      if (premiumUploaderEnabled) loadPremiumContentUploader().catch(() => {});
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(prefetch, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(prefetch, 2000);
    return () => window.clearTimeout(id);
  }, [loading, premiumUploaderEnabled]);

  useEffect(() => {
    audiobooksRef.current = audiobooks;
  }, [audiobooks]);

  useEffect(() => {
    curatedBooksRef.current = curatedBooks;
  }, [curatedBooks]);

  useEffect(() => {
    insightsRef.current = insights;
  }, [insights]);

  useEffect(() => {
    let cancelled = false;

    async function loadBooksLegacy() {
      // Old multi-request waterfall, kept as a fallback while the aggregate
      // endpoint rolls out (and in case it ever fails).
      let userBooks = [];
      try {
        const libraryItems = await wasabiService.listLibrary();
        userBooks = transformLibraryItemsToBooks(libraryItems);
      } catch (libraryError) {
        console.warn('Dashboard: DB library failed, falling back to legacy scan:', libraryError);
        const books = await wasabiService.listAudiobooks(currentUser.uid);
        const metadataItems = await wasabiService.listAudiobookMetadata();
        const transformedUserBooks = books.map(book => ({
          id: book.id,
          name: book.title || 'Untitled',
          chapters: Array.isArray(book.chapters) ? book.chapters : [],
          createdAt: book.createdAt,
          status: book.status,
          statusRaw: book.status,
          progress: book.progress,
          completedChapters: Number(book.completedChapters || 0),
          totalChapters: Number(book.totalChapters || book.chapters?.length || 0),
          jobId: book.id,
          totalDuration: book.totalDuration || 0,
          documentType: book.documentType || '',
          coverUrl: book.coverUrl,
          fallbackQuote: book.fallbackQuote,
          isCurated: false
        }));
        userBooks = mergeMetadataIntoBooks(transformedUserBooks, metadataItems);
      }
      if (cancelled) return;
      setAudiobooks(userBooks);

      const curatedBooksData = await wasabiService.listCuratedAudiobooks();
      const transformedCuratedBooks = transformCuratedBooks(curatedBooksData);
      const curatedWithPlayback = await Promise.all(
        transformedCuratedBooks.map(async (book) => {
          try {
            const playbackState = await wasabiService.getAudiobookPlaybackState(book.id);
            const playbackMetrics = computeCuratedPlaybackMetrics(book, playbackState);
            return { ...book, ...playbackMetrics };
          } catch (playbackError) {
            console.warn(`Dashboard: Failed to fetch curated playback for ${book.id}:`, playbackError);
            return {
              ...book,
              playbackState: String(book.playbackState || 'new'),
              playbackPercent: Number(book.playbackPercent || 0)
            };
          }
        })
      );
      if (cancelled) return;
      setCuratedBooks(curatedWithPlayback);
      // Legacy path has no insights source — keep whatever we already have.
      writeDashboardCache(currentUser.uid, userBooks, curatedWithPlayback, insightsRef.current);
    }

    async function loadBooks() {
      console.log('Dashboard: loadBooks started. User ID:', currentUser?.uid);
      if (currentUser?.uid) {
        // Instant paint: hydrate from the last session's snapshot while the
        // fresh data loads in the background.
        const cached = readDashboardCache(currentUser.uid);
        if (cached) {
          console.log('Dashboard: hydrated from cache');
          setAudiobooks(cached.audiobooks);
          setCuratedBooks(cached.curatedBooks);
          setInsights(cached.insights || null);
          insightsRef.current = cached.insights || null;
          setSubscriptionActive(Boolean(cached.subscriptionActive));
          setLoading(false);
          setLoadingCurated(false);
        } else {
          setLoading(true);
          setLoadingCurated(true);
        }

        try {
          const {
            library, curated, insights: freshInsights, subscriptionActive: freshSubscription
          } = await wasabiService.getDashboard();
          if (cancelled) return;
          const userBooks = transformLibraryItemsToBooks(library);
          const curatedTransformed = transformCuratedBooks(curated);
          const nextInsights = freshInsights || insightsRef.current;
          setAudiobooks(userBooks);
          setCuratedBooks(curatedTransformed);
          setInsights(nextInsights);
          insightsRef.current = nextInsights;
          setSubscriptionActive(Boolean(freshSubscription));
          writeDashboardCache(currentUser.uid, userBooks, curatedTransformed, nextInsights, Boolean(freshSubscription));
        } catch (aggregateError) {
          console.warn('Dashboard: aggregate endpoint failed, using legacy loading:', aggregateError);
          try {
            await loadBooksLegacy();
          } catch (error) {
            console.error('Dashboard: CRITICAL ERROR loading books:', error);
            if (!cancelled && !cached) {
              setAudiobooks([]);
              setCuratedBooks([]);
            }
          }
        } finally {
          if (!cancelled) {
            console.log('Dashboard: loadBooks finished.');
            setLoading(false);
            setLoadingCurated(false);
          }
        }
      } else {
         console.log('Dashboard: No user ID, skipping book load.');
	         setLoading(false); // Ensure loading stops if no user
	         setLoadingCurated(false);
	         setAudiobooks([]); // Clear books if user logs out
	         audiobooksRef.current = [];
	         curatedBooksRef.current = [];
	         setCuratedBooks([]);
	      }
	    }
    loadBooks();
    return () => { cancelled = true; };
  }, [currentUser]); // Keep dependency array as is

  useEffect(() => {
    if (!currentUser?.uid) {
      return undefined;
    }

    let isCancelled = false;
    let timeoutId;

    const hasActiveJobs = (books) => books.some(book => isInProgressStatus(book?.status));
    const getNextDelay = () => {
      const hidden = typeof document !== 'undefined' && document.visibilityState !== 'visible';
      if (hidden) {
        return IDLE_POLL_INTERVAL_MS;
      }
      return hasActiveJobs(audiobooksRef.current) ? ACTIVE_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS;
    };

    const refreshCuratedPlayback = async () => {
      const currentCurated = curatedBooksRef.current;
      if (!Array.isArray(currentCurated) || currentCurated.length === 0) {
        return;
      }

      try {
        const updatedCurated = await Promise.all(
          currentCurated.map(async (book) => {
            try {
              const playbackState = await wasabiService.getAudiobookPlaybackState(book.id);
              const playbackMetrics = computeCuratedPlaybackMetrics(book, playbackState);
              return {
                ...book,
                ...playbackMetrics
              };
            } catch (error) {
              return book;
            }
          })
        );

        if (isCancelled) {
          return;
        }
        curatedBooksRef.current = updatedCurated;
        setCuratedBooks(updatedCurated);
      } catch (error) {
        console.warn('Dashboard: Error refreshing curated playback:', error);
      }
    };

    const refreshMetadata = async () => {
      try {
        // Single aggregate request refreshes user books, curated playback,
        // and cover URLs together.
        const { library, curated, insights: freshInsights } = await wasabiService.getDashboard();
        if (isCancelled) {
          return;
        }
        const nextBooks = stabilizeCoverUrls(
          audiobooksRef.current,
          transformLibraryItemsToBooks(library)
        );
        const curatedTransformed = stabilizeCoverUrls(
          curatedBooksRef.current,
          transformCuratedBooks(curated)
        );
        const nextInsights = freshInsights || insightsRef.current;
        audiobooksRef.current = nextBooks;
        setAudiobooks(nextBooks);
        curatedBooksRef.current = curatedTransformed;
        setCuratedBooks(curatedTransformed);
        insightsRef.current = nextInsights;
        setInsights(nextInsights);
        writeDashboardCache(currentUser.uid, nextBooks, curatedTransformed, nextInsights);
      } catch (aggregateError) {
        console.warn('Dashboard: aggregate refresh failed, using legacy polling:', aggregateError);
        try {
          let nextBooks = [];
          try {
            const libraryItems = await wasabiService.listLibrary();
            nextBooks = stabilizeCoverUrls(
              audiobooksRef.current,
              transformLibraryItemsToBooks(libraryItems)
            );
          } catch (libraryError) {
            console.warn('Dashboard: Polling library failed, using metadata overlay fallback:', libraryError);
            const metadataItems = await wasabiService.listAudiobookMetadata();
            setAudiobooks(prevBooks => {
              const merged = mergeMetadataIntoBooks(prevBooks, metadataItems);
              audiobooksRef.current = merged;
              return merged;
            });
            await refreshCuratedPlayback();
            return;
          }
          if (isCancelled) {
            return;
          }
          audiobooksRef.current = nextBooks;
          setAudiobooks(nextBooks);
          await refreshCuratedPlayback();
        } catch (error) {
          console.error('Dashboard: Error refreshing metadata:', error);
        }
      }
    };

    const scheduleNextRefresh = () => {
      if (isCancelled) {
        return;
      }
      const delay = getNextDelay();
      timeoutId = setTimeout(async () => {
        if (isCancelled) {
          return;
        }
        if (!(typeof document !== 'undefined' && document.visibilityState !== 'visible')) {
          await refreshMetadata();
        }
        scheduleNextRefresh();
      }, delay);
    };

    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        refreshMetadata();
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    scheduleNextRefresh();

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [currentUser?.uid]);

  // Start tutorial for first-time users
  useEffect(() => {
    if (!hasCompletedTutorial && !loading && currentUser) {
      // Add a small delay to ensure UI is ready
      const timer = setTimeout(() => {
        startTour();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [hasCompletedTutorial, loading, currentUser]);

  // Post-submit confirmation toast: acknowledges the credit spend and sets
  // expectations the moment the uploader closes.
  const [submitToast, setSubmitToast] = useState(null);
  const submitToastTimerRef = useRef(null);
  useEffect(() => () => {
    if (submitToastTimerRef.current) window.clearTimeout(submitToastTimerRef.current);
  }, []);

  const handleUploadSuccess = async (result) => {
    setSubmitToast({ title: result.title || 'Your book', storyTime: Boolean(result.storyTime) });
    if (submitToastTimerRef.current) window.clearTimeout(submitToastTimerRef.current);
    submitToastTimerRef.current = window.setTimeout(() => setSubmitToast(null), 8000);
    setAudiobooks(prev => {
      const newAudiobooks = [
        ...prev,
        {
          id: result.id,
          name: result.title || 'Untitled',
          createdAt: new Date().toISOString(),
          status: 'in_queue',
          statusRaw: result.status,
          progress: 0,
          completedChapters: 0,
          totalChapters: result.totalChapters || (Array.isArray(result.chapters) ? result.chapters.length : 0),
          jobId: result.jobId,
          chapters: Array.isArray(result.chapters) ? result.chapters : [],
          totalDuration: 0,
          documentType: result.documentType || '',
          voiceTier: result.voiceTier || '',
          queuePosition: null,
          queueAhead: null,
          queueEtaMinutes: null,
          queueEtaMinMinutes: null,
          queueEtaMaxMinutes: null,
          fallbackQuote: result.fallbackQuote || null,
          isCurated: false
        }
      ];
      audiobooksRef.current = newAudiobooks;
      return newAudiobooks;
    });

    // Add a small delay before closing the uploader modal
    setTimeout(() => {
      setShowUploader(false);
      setShowStoryTime(false);
    }, 300); // 300ms delay
  };

  // Rewards' "Continue the list" (and the celebration's next-book CTA) hand
  // us a curated book id via router state; open it once and clear the state
  // so refresh/back doesn't replay it.
  const location = useLocation();
  const openedFromStateRef = useRef('');
  useEffect(() => {
    const openCuratedId = location.state?.openCuratedId;
    if (!openCuratedId || !currentUser?.uid) return;
    if (openedFromStateRef.current === openCuratedId) return;
    openedFromStateRef.current = openCuratedId;
    navigate(location.pathname, { replace: true, state: {} });
    handleSelectAudio({ id: openCuratedId, status: 'completed' }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, currentUser?.uid]);

  const handleSelectAudio = async (audiobook, isCurated = false, itemKey = null) => {
    let openingKey = '';
    try {
      if (!audiobook) return;

      const normalizedStatus = normalizeStatus(audiobook.status);
      const failed = normalizedStatus === 'failed';
      const inProgress = isInProgressStatus(normalizedStatus);
      const canOpen = isCurated || !inProgress || canOpenInProgressBook(audiobook);
      if (!isCurated && (failed || !canOpen)) return;

      openingKey = `${isCurated ? 'c' : 'u'}:${audiobook.id}`;
      setOpeningAudioKey(openingKey);
      
      setSelectedAudio(itemKey || openingKey);
      // Downloaded books open from the on-device copy when the network is
      // away (or the fetch fails); otherwise the fresh record wins.
      const offlineRecord = offline.getOfflineBook(openingKey);
      let fullData = null;
      if (!offline.online && offlineRecord?.book) {
        fullData = offlineRecord.book;
      } else {
        try {
          fullData = isCurated
            ? await wasabiService.getCuratedAudiobook(audiobook.id)
            : await wasabiService.getAudiobook(currentUser.uid, audiobook.id);
        } catch (fetchError) {
          if (!offlineRecord?.book) throw fetchError;
          fullData = offlineRecord.book;
        }
      }
      setSelectedAudioData(fullData);
    } catch (error) {
      console.error('Error fetching complete audiobook data:', error);
      setSelectedAudio(null);
      setSelectedAudioData(null);
    } finally {
      setOpeningAudioKey((prev) => (prev === openingKey ? null : prev));
    }
  };

  const handleDelete = async (audiobookId) => {
    const deletingBook = audiobooks.find(book => book.id === audiobookId);
    const displayName = formatFileName(deletingBook?.name || 'this audiobook');
    const confirmed = window.confirm(`Delete "${displayName}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setRemovingIds(prev => {
      const next = new Set(prev);
      next.add(audiobookId);
      return next;
    });

    try {
      await wasabiService.deleteAudiobook(currentUser.uid, audiobookId);
      
      if (selectedAudioData && !selectedAudioData.isCurated && selectedAudioData.id === audiobookId) {
        setSelectedAudio(null);
        setSelectedAudioData(null);
      }

      await new Promise(resolve => setTimeout(resolve, 220));
      setAudiobooks(prev => prev.filter(book => book.id !== audiobookId));
    } catch (error) {
      console.error('Error deleting audiobook:', error);
    } finally {
      setRemovingIds(prev => {
        const next = new Set(prev);
        next.delete(audiobookId);
        return next;
      });
      setDeletingId(null);
    }
  };

  const handleDeleteClick = (e, audiobookId) => {
    e.stopPropagation();
    handleDelete(audiobookId);
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setDeletingId(null);
  };

  const handleStartRename = (file) => {
    setEditingId(file.id);
    setEditingName(formatFileName(file.name));
  };

  const handleRename = async (audiobookId) => {
    try {
      if (!editingName.trim()) {
        return;
      }

      // Update manifest using the new method
      await wasabiService.updateManifest(currentUser.uid, audiobookId, {
        title: editingName
      });
      
      // Update local state
      setAudiobooks(prev => prev.map(book =>
        book.id === audiobookId
          ? { ...book, name: editingName }
          : book
      ));
    } catch (error) {
      console.error('Error renaming audiobook:', error);
    } finally {
      setEditingId(null);
      setEditingName('');
    }
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditingName('');
  };

  const formatEmail = (email) => {
    return email?.split('@')[0] || '';
  };

  const handleLogout = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      await logout();
      setShowUserMenu(false);
      navigate('/auth');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleSettingsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowUserMenu(false);
    navigate('/settings');
  };

  const handleInsightsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowUserMenu(false);
    navigate('/insights');
  };

  const handleRewardsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowUserMenu(false);
    navigate('/rewards');
  };

  const handleDashboardTutorial = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowHelpMenu(false);
    resetTutorial();
    startTour();
  };

  const handlePlayerTutorial = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowHelpMenu(false);
    resetPlayerTutorial();
    startPlayerTour();
  };

  const formatFileName = (name) => {
    if (!name) return 'Untitled';
    return name.replace(/\.[^/.]+$/, '');
  };

  const curatedShelfGroups = useMemo(() => {
    const groups = {
      [CURATED_SHELF_KEYS.GENERAL]: [],
      [CURATED_SHELF_KEYS.CLASSICS]: [],
      [CURATED_SHELF_KEYS.SCIENCE]: [],
      [CURATED_SHELF_KEYS.GRAPHIC]: [],
      [CURATED_SHELF_KEYS.SHORT_STORIES]: [],
      [CURATED_SHELF_KEYS.EXPLAINERS]: [],
      [CURATED_SHELF_KEYS.STORY_TIME]: [],
      [CURATED_SHELF_KEYS.STORY_TIME_PREMIUM]: []
    };

    curatedBooks.forEach((book) => {
      const shelf = normalizeCuratedShelf(book?.curatedShelf) || CURATED_SHELF_KEYS.GENERAL;
      if (groups[shelf]) {
        groups[shelf].push(book);
      } else {
        groups[CURATED_SHELF_KEYS.GENERAL].push(book);
      }
    });

    return groups;
  }, [curatedBooks]);

  // Live reading lists: admin-editable Wasabi JSON served by the API, with
  // the bundled static lists as the always-available fallback.
  const [readingLists, setReadingLists] = useState(READING_LISTS);
  useEffect(() => {
    let cancelled = false;
    fetch(getApiUrl('/api/reading-lists'))
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const lists = (data.collections || []).filter(
          (list) => list && list.id && Array.isArray(list.title_keys) && list.title_keys.length
        );
        if (lists.length) setReadingLists(lists);
      })
      .catch(() => { /* static fallback stays in place */ });
    return () => { cancelled = true; };
  }, []);

  const shelfRows = useMemo(() => {
    const query = normalizeSearch(searchQuery);
    const includesQuery = (book) => {
      if (!query) return true;
      const searchText = `${book?.name || ''} ${book?.documentType || ''}`.toLowerCase();
      return searchText.includes(query);
    };
    // Story Time seasons (2 Oct 2026): one tile per series. The tile is the newest finished season (or the first one while it is still being made);
    // the preview sheet lists every season, and a season in progress shows on the tile without blocking the finished ones.
    const groupSeasons = (books) => {
      const groups = {};
      books.forEach((b) => { if (b?.seriesRoot) (groups[b.seriesRoot] = groups[b.seriesRoot] || []).push(b); });
      const placed = new Set();
      const out = [];
      books.forEach((b) => {
        if (!b?.seriesRoot) { out.push(b); return; }
        if (placed.has(b.seriesRoot)) return;
        placed.add(b.seriesRoot);
        const seasons = [...groups[b.seriesRoot]].sort((x, y) => (x.season || 1) - (y.season || 1));
        const isDone = (x) => ['ready', 'completed'].includes(String(x.status || '').toLowerCase());
        const ready = seasons.filter(isDone);
        const lead = ready.length ? ready[ready.length - 1] : seasons[0];
        const making = seasons.find((x) => (x.season || 1) > (lead.season || 1) && !isDone(x) && String(x.status || '').toLowerCase() !== 'failed');
        out.push(seasons.length > 1 ? { ...lead, seasons, seasonInProgress: making ? making.season : null } : b);
      });
      return out;
    };
    const toItems = (books, rowId) =>
      groupSeasons(books)
        .filter(includesQuery)
        .map((book) => {
          const isCuratedBook = Boolean(book?.isCurated);
          const bookKey = `${isCuratedBook ? 'c' : 'u'}:${book.id}`;
          return {
            key: `${rowId}:${bookKey}`,
            bookKey,
            isCurated: isCuratedBook,
            book
          };
        });

    // Playlist rows: reading lists carved out of the classics shelf. A list
    // only becomes a row once it has enough books to look like a shelf;
    // below that it still exists as a rank quest on /rewards.
    const classicsShelfBooks = curatedShelfGroups[CURATED_SHELF_KEYS.CLASSICS] || [];
    const playlistRows = readingLists
      .map((list) => ({
        id: `list-${list.id}`,
        title: list.title,
        hint: list.blurb,
        books: classicsShelfBooks
          .filter((book) => listMatchesTitle(list, book?.title || book?.name))
          // Aspirational order: easy -> hard, matching the /rewards rank ladder.
          .sort(
            (a, b) =>
              listKeyRank(list, a?.title || a?.name) -
              listKeyRank(list, b?.title || b?.name)
          )
      }))
      .filter((row) => row.books.length >= MIN_PLAYLIST_ROW_BOOKS);

    const curatedRows = [
      {
        id: 'story_time_premium',
        title: 'Story Time Premium',
        hint: subscriptionActive
          ? 'Full-cast series, every episode yours to play.'
          : 'Full-cast series. Episode one is free; the rest come with a subscription.',
        books: curatedShelfGroups[CURATED_SHELF_KEYS.STORY_TIME_PREMIUM]
      },
      {
        id: 'story_time_originals',
        title: 'Story Time Originals',
        hint: 'Full-cast series made from a two-line idea. Free for everyone.',
        books: curatedShelfGroups[CURATED_SHELF_KEYS.STORY_TIME]
      },
      {
        id: 'explainers',
        title: 'The Explainer Series',
        hint: 'Guided tours through each shelf, in about 20 minutes',
        books: curatedShelfGroups[CURATED_SHELF_KEYS.EXPLAINERS]
      },
      ...playlistRows,
      {
        id: 'classics',
        title: 'All Classics',
        hint: 'Timeless picks from the BYOPDF team',
        books: curatedShelfGroups[CURATED_SHELF_KEYS.CLASSICS]
      },
      {
        id: 'science',
        title: 'Interesting Science Papers',
        hint: 'Assigned manually from curated metadata',
        books: curatedShelfGroups[CURATED_SHELF_KEYS.SCIENCE]
      },
      {
        id: 'graphic',
        title: 'Graphic Book Excerpts',
        hint: 'Assigned manually from curated metadata',
        books: curatedShelfGroups[CURATED_SHELF_KEYS.GRAPHIC]
      },
      {
        id: 'short-stories',
        title: 'Short Stories',
        hint: 'Assigned manually from curated metadata',
        books: curatedShelfGroups[CURATED_SHELF_KEYS.SHORT_STORIES]
      }
    ]
      .map((row) => ({
        id: row.id,
        title: row.title,
        hint: row.hint,
        loading: loadingCurated,
        sourceCount: Array.isArray(row.books) ? row.books.length : 0,
        items: toItems(row.books || [], row.id)
      }))
      .filter((row) => row.sourceCount > 0 && row.items.length > 0);

    // Order the user's library per the selected sort. 'recent' keeps the
    // upstream createdAt ordering; 'listened' floats recently-played books.
    const sortedAudiobooks = librarySort === 'listened'
      ? [...audiobooks].sort((a, b) => {
        const aPlayed = Date.parse(a?.lastPlayedAt || '') || 0;
        const bPlayed = Date.parse(b?.lastPlayedAt || '') || 0;
        if (bPlayed !== aPlayed) return bPlayed - aPlayed;
        return (Date.parse(b?.createdAt || '') || 0) - (Date.parse(a?.createdAt || '') || 0);
      })
      : audiobooks;

    const emptyRowCopy = {
      emptyTitle: query ? 'No matches in your audiobooks' : 'No audiobooks yet',
      emptyDescription: query
        ? 'Try a shorter query or clear search.'
        : 'Create your first audiobook using the New Book button.'
    };

    let userRows;
    if (librarySort === 'type' && audiobooks.length > 0) {
      const byType = {};
      sortedAudiobooks.forEach((book) => {
        const raw = String(book?.documentType || '').trim().toLowerCase();
        const shelfType = DOC_TYPE_SHELVES.some((shelf) => shelf.type === raw) ? raw : 'book';
        (byType[shelfType] = byType[shelfType] || []).push(book);
      });
      userRows = DOC_TYPE_SHELVES
        .map((shelf, index) => ({
          id: `my-${shelf.type}`,
          title: shelf.title,
          hint: shelf.hint,
          loading,
          dataTour: index === 0 ? 'library-section' : undefined,
          showSortControl: true,
          items: toItems(byType[shelf.type] || [], `my-${shelf.type}`),
          sourceCount: (byType[shelf.type] || []).length,
          ...emptyRowCopy
        }))
        .filter((row) => row.sourceCount > 0 && row.items.length > 0);
      // Search can empty every type shelf; fall back to one row so the
      // empty state (and the sort control) stays visible.
      if (userRows.length === 0) {
        userRows = [{
          id: 'my-audiobooks',
          title: 'Your Audiobooks',
          hint: 'Books you uploaded and generated',
          loading,
          dataTour: 'library-section',
          showSortControl: true,
          items: [],
          ...emptyRowCopy
        }];
      } else {
        userRows[0] = { ...userRows[0], dataTour: 'library-section' };
      }
    } else {
      userRows = [{
        id: 'my-audiobooks',
        title: 'Your Audiobooks',
        hint: 'Books you uploaded and generated',
        loading,
        dataTour: 'library-section',
        showSortControl: true,
        items: toItems(sortedAudiobooks, 'my-audiobooks'),
        ...emptyRowCopy
      }];
    }

    // Books stored on this device: first shelf, and the only one that is
    // guaranteed to open with no network.
    const offlineBooks = (offline.standalone ? Object.values(offline.records || {}) : [])
      .filter((record) => record?.status === 'complete' && record.summary)
      .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))
      .map((record) => {
        const live = record.isCurated
          ? null
          : audiobooks.find((book) => String(book?.id) === String(record.id));
        return { ...record.summary, ...(live || {}), isCurated: record.isCurated };
      });
    const offlineRows = offlineBooks.length
      ? [{
        id: 'on-this-device',
        title: offline.online ? 'On this device' : 'Available offline',
        hint: offline.online ? 'Downloaded for flights and no-signal listening' : 'You are offline. These books still play.',
        loading: false,
        dataTour: 'offline-section',
        showSortControl: false,
        items: toItems(offlineBooks, 'on-this-device'),
        emptyTitle: '',
        emptyDescription: ''
      }]
      : [];

    return [...offlineRows, ...userRows, ...curatedRows];
  }, [audiobooks, curatedShelfGroups, loading, loadingCurated, searchQuery, librarySort, readingLists, offline.records, offline.online, offline.standalone, subscriptionActive]);

  const totalVisibleResults = useMemo(
    () => shelfRows.reduce((total, row) => total + row.items.length, 0),
    [shelfRows]
  );

  // Most recently listened, unfinished book — powers the habit hero's
  // "Continue listening" slot. 'in_progress' already excludes finished books.
  const continueBook = useMemo(() => {
    const candidates = [];
    audiobooks.forEach((book) => {
      const time = Date.parse(book?.lastPlayedAt || '');
      const status = normalizeStatus(book?.status);
      if (
        book?.playbackState === 'in_progress' &&
        Number.isFinite(time) &&
        !isInProgressStatus(status) &&
        status !== 'failed'
      ) {
        candidates.push({ book, time });
      }
    });
    curatedBooks.forEach((book) => {
      const time = Date.parse(book?.playback?.updatedAt || '');
      if (book?.playbackState === 'in_progress' && Number.isFinite(time)) {
        candidates.push({ book, time });
      }
    });
    candidates.sort((a, b) => b.time - a.time);
    return candidates[0]?.book || null;
  }, [audiobooks, curatedBooks]);

  const handleSaveGoal = async (minutes) => {
    const previous = insightsRef.current;
    const optimistic = { ...(previous || {}), goalWeeklyMinutes: minutes };
    setInsights(optimistic);
    insightsRef.current = optimistic;
    try {
      await wasabiService.setGoal(minutes);
      writeDashboardCache(currentUser.uid, audiobooksRef.current, curatedBooksRef.current, optimistic);
    } catch (error) {
      console.error('Dashboard: failed to save weekly goal:', error);
      setInsights(previous);
      insightsRef.current = previous;
    }
  };

  const hasSearchQuery = normalizeSearch(searchQuery).length > 0;
  const isSearchOpen = isSearchExpanded || hasSearchQuery;

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const handleSearchOpen = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchClose = () => {
    setSearchQuery('');
    setIsSearchExpanded(false);
  };

  const handleSearchBlur = () => {
    if (!hasSearchQuery) {
      setIsSearchExpanded(false);
    }
  };

  const setShelfRef = (rowId, node) => {
    if (node) {
      shelfRefs.current[rowId] = node;
    } else {
      delete shelfRefs.current[rowId];
    }
  };

  const scrollShelf = (rowId, direction) => {
    const row = shelfRefs.current[rowId];
    if (!row) return;
    const distance = Math.max(260, Math.round(row.clientWidth * 0.85));
    row.scrollBy({ left: distance * direction, behavior: 'smooth' });
  };

  const handleShelfWheel = (event, rowId) => {
    const row = shelfRefs.current[rowId];
    if (!row) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    event.preventDefault();
    row.scrollBy({ left: event.deltaY, behavior: 'auto' });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.userInfo}>
            <div className={styles.brandLockup}>
              <img
                src={isDark ? '/logo_mark_cream.png' : '/logo_mark_black.png'}
                alt=""
                aria-hidden="true"
                className={styles.brandMark}
              />
              <h1>BYOPDF</h1>
            </div>
            <p>{formatEmail(currentUser?.email)}</p>
          </div>
          <div className={styles.headerRight}>
            <div data-tour="credits-display">
              <CreditDisplay compact />
            </div>
            <button
              onClick={toggleTheme}
              className={styles.themeToggle}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <RiSunLine size={20} aria-hidden="true" /> : <RiMoonLine size={20} aria-hidden="true" />}
            </button>
            
            <div className={styles.userMenuContainer}>
              <button
                onClick={() => setShowHelpMenu(!showHelpMenu)}
                className={styles.userButton}
                title="Tutorials & Help"
                aria-label="Tutorials and help"
                aria-expanded={showHelpMenu}
              >
                <RiQuestionLine size={20} aria-hidden="true" />
              </button>

              {showHelpMenu && (
                <>
                  <div className={styles.userMenu}>
                    <div className={styles.userMenuHeader}>
                      <p>Tutorials & Help</p>
                    </div>

                    <div className={styles.userMenuContent}>
                      <button
                        onClick={handleDashboardTutorial}
                        className={styles.menuItem}
                      >
                        <RiHome4Line size={20} />
                        Dashboard Tutorial
                      </button>

                      <button
                        onClick={handlePlayerTutorial}
                        className={styles.menuItem}
                      >
                        <RiBookOpenLine size={20} />
                        Audio Player Tutorial
                      </button>

                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowHelpMenu(false);
                          navigate('/faq');
                        }}
                        className={styles.menuItem}
                      >
                        <RiQuestionLine size={20} />
                        FAQ
                      </button>
                    </div>
                  </div>
                  <div
                    className={styles.overlay}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHelpMenu(false);
                    }}
                  />
                </>
              )}
            </div>
            
            <div className={styles.userMenuContainer}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className={styles.userButton}
                aria-label="Account menu"
                aria-expanded={showUserMenu}
              >
                <RiUser3Line size={20} aria-hidden="true" />
              </button>

              {showUserMenu && (
                <>
                  <div className={styles.userMenu}>
                    <div className={styles.userMenuHeader}>
                      <p>Signed in as</p>
                      <p>{currentUser?.email}</p>
                    </div>

                    <div className={styles.userMenuContent}>
                      <button
                        onClick={handleInsightsClick}
                        className={styles.menuItem}
                      >
                        <RiBarChart2Line size={20} />
                        Insights
                      </button>

                      <button
                        onClick={handleRewardsClick}
                        className={styles.menuItem}
                      >
                        <RiMedalLine size={20} />
                        Rewards
                      </button>

                      <button
                        onClick={handleSettingsClick}
                        className={styles.menuItem}
                      >
                        <RiSettings4Line size={20} />
                        Settings
                      </button>

                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowUserMenu(false);
                          navigate('/purchase');
                        }}
                        className={styles.menuItem}
                      >
                        <RiShoppingBag3Line size={20} />
                        Plans &amp; Pricing
                      </button>

                      <button
                        onClick={handleLogout}
                        className={`${styles.menuItem} ${styles.danger}`}
                      >
                        <RiLogoutBoxRLine size={20} />
                        Logout
                      </button>
                    </div>
                  </div>
                  <div
                    className={styles.overlay}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowUserMenu(false);
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.libraryShell}>
          <div className={styles.topActions}>
            <div className={`${styles.searchControl} ${isSearchOpen ? styles.searchControlExpanded : ''}`}>
              <button
                type="button"
                className={styles.searchToggleButton}
                onClick={handleSearchOpen}
                aria-label="Open search"
              >
                <RiSearchLine size={18} />
              </button>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className={styles.searchInput}
                onBlur={handleSearchBlur}
                placeholder="Search books and authors…"
                aria-label="Search audiobook shelves"
              />
              {isSearchOpen && (
                <button
                  type="button"
                  className={styles.searchCloseButton}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleSearchClose}
                  aria-label="Close search"
                >
                  <RiCloseLine size={16} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowUploader(true)}
              className={styles.addButton}
              title="Create New"
              data-tour="add-book-button"
            >
              <RiAddLine size={20} />
              New Book
            </button>
            <button
              onClick={() => setShowStoryTime(true)}
              className={`${styles.addButton} ${styles.storyTimeButton}`}
              title="Turn an idea into an 8-episode audio series"
              data-tour="story-time-button"
            >
              <RiSparkling2Line size={18} />
              Story Time
            </button>
          </div>

          {hasSearchQuery && (
            <p className={styles.searchSummary}>
              Showing {totalVisibleResults} result{totalVisibleResults === 1 ? '' : 's'} for "{searchQuery.trim()}"
            </p>
          )}

          {!hasSearchQuery && (
            <StoryTimeHero
              loading={loadingCurated && curatedBooks.length === 0}
              premiumBooks={curatedShelfGroups[CURATED_SHELF_KEYS.STORY_TIME_PREMIUM]}
              freeBooks={curatedShelfGroups[CURATED_SHELF_KEYS.STORY_TIME]}
              subscriptionActive={subscriptionActive}
              openingKey={openingAudioKey}
              onOpenBook={(book) => handleSelectAudio(book, true)}
              onMakeYourOwn={() => setShowStoryTime(true)}
            />
          )}

          {!hasSearchQuery && (
            <HabitHero
              compact
              loading={loading && !insights && !continueBook}
              insights={insights}
              continueBook={continueBook}
              continueTitle={continueBook ? formatFileName(continueBook.name) : ''}
              isOpening={continueBook ? openingAudioKey === `${continueBook.isCurated ? 'c' : 'u'}:${continueBook.id}` : false}
              onResume={() => continueBook && handleSelectAudio(continueBook, continueBook.isCurated)}
              onStartBrowsing={() => setShowUploader(true)}
              onSaveGoal={handleSaveGoal}
            />
          )}

          <InstallBanner />

          {shelfRows.map((row) => (
            <section key={row.id} className={styles.shelfSection}>
              <div className={styles.shelfHeader}>
                <div>
                  <h3 className={styles.shelfTitle}>{row.title}</h3>
                  <p className={styles.shelfHint}>{row.hint}</p>
                </div>
                {row.showSortControl && row.dataTour === 'library-section' && (() => {
                  const sortIndex = Math.max(0, LIBRARY_SORT_OPTIONS.findIndex((o) => o.value === librarySort));
                  const currentSort = LIBRARY_SORT_OPTIONS[sortIndex];
                  const nextSort = LIBRARY_SORT_OPTIONS[(sortIndex + 1) % LIBRARY_SORT_OPTIONS.length];
                  return (
                    <button
                      type="button"
                      className={styles.sortCycle}
                      onClick={() => handleLibrarySortChange(nextSort.value)}
                      title={`Sorted by ${currentSort.label.toLowerCase()}. Tap for ${nextSort.label.toLowerCase()}`}
                      aria-label={`Library sorted by ${currentSort.label}. Switch to ${nextSort.label}.`}
                    >
                      <RiArrowUpDownLine aria-hidden="true" />
                      <span>{currentSort.label}</span>
                    </button>
                  );
                })()}
                {!row.loading && row.items.length > 1 && (
                  <div className={styles.shelfControls}>
                    <button
                      type="button"
                      className={styles.shelfScrollButton}
                      onClick={() => scrollShelf(row.id, -1)}
                      aria-label={`Scroll ${row.title} left`}
                    >
                      <RiArrowLeftSLine size={18} />
                    </button>
                    <button
                      type="button"
                      className={styles.shelfScrollButton}
                      onClick={() => scrollShelf(row.id, 1)}
                      aria-label={`Scroll ${row.title} right`}
                    >
                      <RiArrowRightSLine size={18} />
                    </button>
                  </div>
                )}
              </div>

              {row.loading ? (
                <div className={styles.shelfViewport} aria-busy="true" aria-label="Loading your library">
                  <div className={styles.shelfTrack}>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className={styles.shelfCard}>
                        <div className={styles.skeletonCard} style={{ animationDelay: `${i * 0.08}s` }}>
                          <div className={styles.skeletonCover} />
                          <div className={styles.skeletonLine} />
                          <div className={styles.skeletonChip} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : row.items.length === 0 ? (
                <div className={styles.shelfEmpty}>
                  {/* A small etched shelf so the empty state matches the
                      bookplate language instead of reading as an error. */}
                  <svg className={styles.shelfEmptyArt} viewBox="0 0 120 60" aria-hidden="true">
                    <g fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="28" y="14" width="11" height="34" rx="1.5" />
                      <rect x="42" y="9" width="12" height="39" rx="1.5" />
                      <path d="M60 48 L66 10 L77 12 L71 48 Z" />
                      <path d="M31.5 20 h4 M45.5 15 h5" strokeWidth="1.6" />
                    </g>
                    <rect x="80" y="24" width="10" height="24" rx="1.5" fill="none" stroke="var(--primary-color)" strokeWidth="2.4" strokeDasharray="3 3" />
                    <path d="M14 50 h92" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" />
                    <path d="M20 55 h80" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" opacity="0.4" />
                  </svg>
                  <h3>{row.emptyTitle}</h3>
                  <p>{row.emptyDescription}</p>
                </div>
              ) : (
                <div className={styles.shelfViewport} data-tour={row.dataTour}>
                  <div
                    className={styles.shelfTrack}
                    ref={(node) => setShelfRef(row.id, node)}
                    onWheel={(event) => handleShelfWheel(event, row.id)}
                  >
                    {row.items.map((item) => (
                      <div key={item.key} className={styles.shelfCard}>
                        <AudioItem
                          file={item.book}
                          index={item.key}
                          selectedAudio={selectedAudio}
                          editingId={editingId}
                          editingName={editingName}
                          hoveredItem={hoveredItem}
                          deletingId={deletingId}
                          isPlaying={isPlaying}
                          onSelect={() => handleSelectAudio(item.book, item.isCurated, item.key)}
                          onSelectBook={(b) => handleSelectAudio(b, item.isCurated, item.key)}
                          onHover={setHoveredItem}
                          onRename={handleRename}
                          onCancelRename={handleCancelRename}
                          onDelete={handleDelete}
                          onCancelDelete={handleCancelDelete}
                          onStartRename={handleStartRename}
                          onDeleteClick={handleDeleteClick}
                          setEditingName={setEditingName}
                          isCurated={item.isCurated}
                          isRemoving={removingIds.has(item.book.id)}
                          isOpening={openingAudioKey === item.bookKey}
                          isDisabled={!item.isCurated && (normalizeStatus(item.book.status) === 'failed' || (isInProgressStatus(item.book.status) && !canOpenInProgressBook(item.book)))}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          ))}

          {selectedAudio !== null && selectedAudioData && (
            <AudioPlayer
              audioFile={selectedAudioData}
              userId={currentUser?.uid}
              subscriptionActive={subscriptionActive}
              onPlaybackStateChange={setIsPlaying}
              onAddAnotherBook={() => setShowUploader(true)}
              onOpenCuratedBook={(bookId) => handleSelectAudio({ id: bookId, status: 'completed' }, true)}
              onClose={() => { setSelectedAudio(null); setSelectedAudioData(null); }}
              autoMinimize={showUploader}
            />
          )}
        </div>
      </div>

      {showUploader && (
        <div className={styles.takeover} role="dialog" aria-modal="true" aria-label="Create audiobook">
          <header className={styles.takeoverHeader}>
            <div className={styles.takeoverBrand}>
              <span className={styles.takeoverEyebrow}>BYOPDF</span>
              <span className={styles.takeoverTitle}>New Audiobook</span>
            </div>
            {premiumUploaderEnabled && (
              <div className={styles.uploaderModeRow}>
                <button
                  type="button"
                  className={`${styles.uploaderModeButton} ${uploaderMode === 'standard' ? styles.uploaderModeButtonActive : ''}`}
                  onClick={() => setUploaderMode('standard')}
                >
                  Standard
                </button>
                <button
                  type="button"
                  className={`${styles.uploaderModeButton} ${uploaderMode === 'premium' ? styles.uploaderModeButtonActive : ''}`}
                  onClick={() => setUploaderMode('premium')}
                >
                  Premium Voice
                </button>
              </div>
            )}
            <button
              onClick={() => {
                setShowUploader(false);
                setUploaderMode('standard');
              }}
              className={styles.takeoverClose}
              aria-label="Close"
              type="button"
            >
              <RiCloseLine size={22} />
            </button>
          </header>
          <div className={styles.takeoverBody}>
            <Suspense fallback={<div className={styles.takeoverLoading}><ByopdfLoader size={56} /></div>}>
              {premiumUploaderEnabled && uploaderMode === 'premium' ? (
                <PremiumContentUploader onUploadSuccess={handleUploadSuccess} />
              ) : (
                <ContentUploader onUploadSuccess={handleUploadSuccess} />
              )}
            </Suspense>
          </div>
        </div>
      )}

      {showStoryTime && (
        <div className={styles.takeover} role="dialog" aria-modal="true" aria-label="Story Time">
          <header className={styles.takeoverHeader}>
            <div className={styles.takeoverBrand}>
              <span className={styles.takeoverEyebrow}>BYOPDF</span>
              <span className={styles.takeoverTitle}>Story Time</span>
            </div>
            <button onClick={() => setShowStoryTime(false)} className={styles.takeoverClose} aria-label="Close" type="button">
              <RiCloseLine size={22} />
            </button>
          </header>
          <div className={styles.takeoverBody}>
            <StoryTimeWizard onCreated={handleUploadSuccess} onCancel={() => setShowStoryTime(false)} />
          </div>
        </div>
      )}

      {submitToast && (
        <div
          className={styles.submitToast}
          role="status"
          onClick={() => setSubmitToast(null)}
        >
          <span className={styles.submitToastIcon} aria-hidden="true">🎙️</span>
          <div>
            {submitToast.storyTime ? (<><strong>Your story</strong> is being written.</>) : (<><strong>{submitToast.title}</strong> is off to the studio.</>)}
            <span className={styles.submitToastSub}>
              {submitToast.storyTime
                ? "Its title and poster appear here in a couple of minutes. The full season takes about 45 minutes; we'll email you."
                : "First chapter usually takes a few minutes. We'll email you when the full book is ready."}
            </span>
          </div>
        </div>
      )}

      <AppDock />

      <Suspense fallback={null}>
        <InteractiveTour />
      </Suspense>
    </div>
  );
}

export default Dashboard;
